/**
 * Indian Standard Time (IST - Asia/Kolkata, UTC+05:30) Date & Time Utilities
 * Standard format for dates across the platform: DD/MM/YYYY
 */

export const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Format a date string, timestamp, or Date object into DD/MM/YYYY in IST.
 * Example: 20/08/2026
 */
export function formatDateIST(dateInput?: string | number | Date | null): string {
  if (!dateInput) {
    dateInput = new Date();
  }
  const d = typeof dateInput === 'string' || typeof dateInput === 'number' ? new Date(dateInput) : dateInput;
  if (isNaN(d.getTime())) {
    return '';
  }

  // en-GB locale formats as DD/MM/YYYY with exact 2-digit day and month
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return formatter.format(d);
}

/**
 * Format date & time into DD/MM/YYYY, hh:mm A in IST.
 * Example: 20/08/2026, 01:15 AM
 */
export function formatDateTimeIST(dateInput?: string | number | Date | null): string {
  if (!dateInput) {
    dateInput = new Date();
  }
  const d = typeof dateInput === 'string' || typeof dateInput === 'number' ? new Date(dateInput) : dateInput;
  if (isNaN(d.getTime())) {
    return '';
  }

  const dateStr = formatDateIST(d);
  
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  return `${dateStr}, ${timeFormatter.format(d)}`;
}

/**
 * Format time only into hh:mm A in IST.
 * Example: 01:15 AM
 */
export function formatTimeIST(dateInput?: string | number | Date | null): string {
  if (!dateInput) {
    dateInput = new Date();
  }
  const d = typeof dateInput === 'string' || typeof dateInput === 'number' ? new Date(dateInput) : dateInput;
  if (isNaN(d.getTime())) {
    return '';
  }

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  return timeFormatter.format(d);
}

/**
 * Returns current timestamp in IST as ISO string
 */
export function getNowISTISOString(): string {
  return new Date().toISOString();
}

/**
 * Parse a DD/MM/YYYY string into a Date object
 */
export function parseDateDDMMYYYY(str: string): Date | null {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split('/');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  const d = new Date(year, month, day);
  return isNaN(d.getTime()) ? null : d;
}
